import { useQuery } from '@tanstack/react-query'
import { hasLiveApi } from '@/api/client'
import { getMemberBalance, type MemberBalance } from '@/api/map/reportMeta'
import { isUnreachableApiError } from '@/api/unreachable'
import { keys } from '@/api/hooks/shared/keys'
import { useSession } from '@/api/hooks/shared/useSession'
import {
  displayToInrMajor,
  parseCurrencyType,
} from '@/lib/currency'

/**
 * Lab `Member/getBalance`; demo derives the same fields from the session
 * account, while live mode always reads the canonical Agent-API money view.
 */
export function useMemberBalance() {
  const { data: session } = useSession()
  return useQuery({
    queryKey: keys.memberBalance,
    enabled: Boolean(session),
    queryFn: async (): Promise<MemberBalance | null> => {
      if (hasLiveApi()) {
        try {
          return await getMemberBalance()
        } catch (error) {
          if (isUnreachableApiError(error)) return null
          throw error
        }
      }

      const account = session!.account
      const currency = parseCurrencyType(account.currencyType)
      return {
        balance: displayToInrMajor(account.balance, currency),
        available: displayToInrMajor(
          account.creditLimit - account.distributedBalance - account.exposure,
          currency,
        ),
        creditLimit: displayToInrMajor(account.creditLimit, currency),
        distributed: displayToInrMajor(account.distributedBalance, currency),
        exposure: displayToInrMajor(account.exposure, currency),
        balanceUp: displayToInrMajor(account.settlement, currency),
        currency,
      }
    },
    staleTime: 15_000,
  })
}
